import * as React from 'react';
import { useState, useMemo, useCallback, useEffect } from 'react';

import type Search from 'views/logic/search/Search';
import type {
  PreviewJobStatus,
  DataLakeOption,
  DataLakeInfoArray,
  DataLakeFieldsFetchQuery,
} from 'data-lake/preview/Types';
import useFeature from 'hooks/useFeature';
import { VALID_ACTIVE_SEARCH_STATUSES } from 'data-lake/preview/Constants';
import usePluginEntities from 'hooks/usePluginEntities';
import type { DataLakePlugin } from 'data-lake/Types';
import { INTERNAL_DATA_LAKE_ID } from 'data-lake/preview/storage-types/internal/Constants';
import type { QueryString } from 'views/logic/queries/types';
import findDataLakeIdInSearchType from 'data-lake/preview/utils/findDataLakeIdInSearchType';
import type { SelectedDataLakesContextValue } from 'data-lake/contexts/SelectedDataLakesContext';
import SelectedDataLakesContext from 'data-lake/contexts/SelectedDataLakesContext';
import { INTERNAL_DATA_LAKE_INFO } from 'data-lake/Constants';

const isValidDataLakeId = (activeDataLakes: DataLakeInfoArray, dataLakeId: string) =>
  activeDataLakes.some(({ id }) => id === dataLakeId);

const isValidQueryTypeForDataLake = (
  dataLakePlugins: Array<DataLakePlugin>,
  type: string,
): type is QueryString['type'] => dataLakePlugins.some(({ queryType }) => queryType === type);

const initiallySelectedDataLakeId = (
  featureIsEnabled: boolean,
  initialUrlParams: { dataLakeId: string },
  activeSearch: Search,
  searchJobStatus: PreviewJobStatus,
  activeDataLakes: DataLakeInfoArray,
  dataLakePlugins: Array<DataLakePlugin>,
) => {
  if (!featureIsEnabled) {
    return INTERNAL_DATA_LAKE_ID;
  }

  // get active data lake id from URL query params
  if (initialUrlParams?.dataLakeId && isValidDataLakeId(activeDataLakes, initialUrlParams?.dataLakeId)) {
    return initialUrlParams.dataLakeId;
  }

  // get active data lake id from active search
  if (VALID_ACTIVE_SEARCH_STATUSES.includes(searchJobStatus) && activeSearch) {
    const query = activeSearch.queries.first();
    const queryType = query.query.type;
    if (isValidQueryTypeForDataLake(dataLakePlugins, queryType)) {
      return findDataLakeIdInSearchType(activeSearch);
    }
  }

  if (activeDataLakes?.length > 0) {
    if (activeDataLakes.find(({ id: dataLakeId }) => dataLakeId === INTERNAL_DATA_LAKE_ID)) {
      return INTERNAL_DATA_LAKE_ID;
    }

    return activeDataLakes[0].id;
  }

  throw new Error('Expected at least one data lake to be configured.');
};

type Props = React.PropsWithChildren<{
  activeDataLakes: DataLakeInfoArray;
  activeSearch: Search;
  initialUrlParams: { dataLakeId: string };
  searchJobStatus: PreviewJobStatus;
  setDataLakeIdUrlParam: (dataLakeId: string, resetOtherParams?: boolean) => void;
}>;

const PreviewConfiguredDataLakesProvider = ({
  children = undefined,
  initialUrlParams,
  activeSearch,
  searchJobStatus,
  activeDataLakes,
  setDataLakeIdUrlParam,
}: Props) => {
  const featureIsEnabled = useFeature('external_data_lake_search');
  const dataLakePlugins = usePluginEntities('dataLakeStorageTypes');
  const [selectedDataLakeId, selectDataLakeId] = useState<DataLakeOption['value']>(
    initiallySelectedDataLakeId(
      featureIsEnabled,
      initialUrlParams,
      activeSearch,
      searchJobStatus,
      activeDataLakes,
      dataLakePlugins,
    ),
  );
  const [dataLakeFieldsFetchQuery, setDataLakeFieldsFetchQuery] = useState<DataLakeFieldsFetchQuery>(null);

  const selectDataLake = useCallback(
    (newDataLakeId: string) => {
      setDataLakeFieldsFetchQuery(null);
      setDataLakeIdUrlParam(newDataLakeId, true);

      return selectDataLakeId(newDataLakeId);
    },
    [setDataLakeIdUrlParam],
  );

  useEffect(() => {
    setDataLakeIdUrlParam(selectedDataLakeId);
  }, [selectedDataLakeId, setDataLakeIdUrlParam]);

  const contextValue = useMemo<SelectedDataLakesContextValue>(
    () => ({
      selectDataLake,
      selectedDataLake: activeDataLakes.find(({ id }) => id === selectedDataLakeId) ?? INTERNAL_DATA_LAKE_INFO,
      dataLakeFieldsFetchQuery,
      setDataLakeFieldsFetchQuery,
    }),
    [activeDataLakes, dataLakeFieldsFetchQuery, selectDataLake, selectedDataLakeId],
  );

  return <SelectedDataLakesContext.Provider value={contextValue}>{children}</SelectedDataLakesContext.Provider>;
};

export default PreviewConfiguredDataLakesProvider;
